import { useState, useRef, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { ArrowLeftRight, Loader2, Trash2, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { PageHeader } from "@/components/layout";
import { AppLink } from "@/components/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRelativeTime } from "@/lib/utils";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useProject } from "@/components/projects";

export const Route = createFileRoute("/comparisons/")({
  component: ComparisonsPage,
});

function useInlineEdit(initialValue: string, onSave: (value: string) => Promise<void>) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== initialValue) {
      try {
        await onSave(trimmed);
      } catch {
        setValue(initialValue);
      }
    } else {
      setValue(initialValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    } else if (e.key === "Escape") {
      setValue(initialValue);
      setIsEditing(false);
    }
  };

  return {
    isEditing,
    setIsEditing,
    value,
    setValue,
    inputRef,
    handleSave,
    handleKeyDown,
  };
}

export function ComparisonsPage() {
  const { projectId, project, isLoading: isProjectLoading } = useProject();
  const { data: comparisons } = useQuery({
    ...convexQuery(api.scheduleComparisons.listWithStats, { projectId: projectId! }),
    enabled: !!projectId,
  });
  const navigate = useAppNavigate();
  const removeComparison = useMutation(api.scheduleComparisons.remove);
  const updateName = useMutation(api.scheduleComparisons.updateName);
  const [deletingComparisonIds, setDeletingComparisonIds] = useState<Set<string>>(
    () => new Set()
  );

  if (isProjectLoading || comparisons === undefined) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="RevPilot"
          description={project?.name ?? "Loading..."}
        />
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const handleNewRevPilot = () => {
    navigate({ to: "/revPilot/new", title: "New RevPilot" });
  };

  const handleDeleteComparison = async (
    comparisonId: Id<"revPilotScheduleComparisons">
  ) => {
    setDeletingComparisonIds((current) => {
      const next = new Set(current);
      next.add(String(comparisonId));
      return next;
    });

    try {
      await removeComparison({ comparisonId });
      toast.success("Comparison deleted");
    } catch (error) {
      toast.error("Failed to delete comparison");
      console.error(error);
    } finally {
      setDeletingComparisonIds((current) => {
        const next = new Set(current);
        next.delete(String(comparisonId));
        return next;
      });
    }
  };

  const handleUpdateName = async (
    comparisonId: Id<"revPilotScheduleComparisons">,
    name: string
  ) => {
    try {
      await updateName({ comparisonId, name });
      toast.success("Name updated");
    } catch (error) {
      toast.error("Failed to update name");
      console.error(error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="RevPilot"
        description={project?.name ?? "Select a project"}
        actions={
          <Button onClick={handleNewRevPilot}>
            <ArrowLeftRight className="size-4" />
            New RevPilot
          </Button>
        }
      />

      {comparisons.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No RevPilot comparisons yet. Create a RevPilot comparison from project document revisions to compare schedules or plan sets.
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[70px]">
                  <span className="sr-only">View</span>
                </TableHead>
                <TableHead>Comparison</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Changed Rows</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisons.map((comparison: (typeof comparisons)[number]) => {
                const isDeleting = deletingComparisonIds.has(String(comparison._id));

                return (
                  <ComparisonRow
                    key={comparison._id}
                    comparison={comparison}
                    isDeleting={isDeleting}
                    onDelete={handleDeleteComparison}
                    onUpdateName={handleUpdateName}
                  />
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

interface ComparisonRowProps {
  comparison: {
    _id: string;
    name: string;
    comparisonKind?: string;
    status: string;
    stats?: { changedRows?: number } | null;
    createdAt: number;
  };
  isDeleting: boolean;
  onDelete: (comparisonId: Id<"revPilotScheduleComparisons">) => Promise<void>;
  onUpdateName: (comparisonId: Id<"revPilotScheduleComparisons">, name: string) => Promise<void>;
}

function ComparisonRow({ comparison, isDeleting, onDelete, onUpdateName }: ComparisonRowProps) {
  const inlineEdit = useInlineEdit(comparison.name, (name) =>
    onUpdateName(comparison._id as Id<"revPilotScheduleComparisons">, name)
  );

  return (
    <TableRow className="group">
      <TableCell>
        <AppLink
          to="/revPilot/$comparisonId"
          params={{ comparisonId: comparison._id }}
          title={comparison.name}
        >
          <Button variant="ghost" size="icon-sm" className="h-8 w-8">
            <Eye className="size-4" />
            <span className="sr-only">View {comparison.name}</span>
          </Button>
        </AppLink>
      </TableCell>
      <TableCell className="font-medium">
        {inlineEdit.isEditing ? (
          <Input
            ref={inlineEdit.inputRef}
            value={inlineEdit.value}
            onChange={(e) => inlineEdit.setValue(e.target.value)}
            onBlur={() => void inlineEdit.handleSave()}
            onKeyDown={inlineEdit.handleKeyDown}
            className="h-8 w-full max-w-xs"
          />
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-foreground">{comparison.name}</span>
            <Button
              variant="ghost"
              size="icon-sm"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 focus:opacity-100"
              onClick={() => inlineEdit.setIsEditing(true)}
            >
              <Pencil className="size-3" />
              <span className="sr-only">Edit name</span>
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell>
        <Badge variant="outline">
          {comparison.comparisonKind === "plan_set" ? "Plan Set" : "Schedule"}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge
          variant={
            comparison.status === "completed"
              ? "default"
              : comparison.status === "failed"
                ? "destructive"
                : "secondary"
          }
        >
          {comparison.status}
        </Badge>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {comparison.stats?.changedRows ?? 0}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatRelativeTime(comparison.createdAt)}
      </TableCell>
      <TableCell className="text-right">
        <DeleteConfirmationDialog
          title="Delete Comparison"
          description={`Are you sure you want to delete "${comparison.name}"? This action cannot be undone.`}
          onConfirm={() => {
            void onDelete(comparison._id as Id<"revPilotScheduleComparisons">);
          }}
          disabled={isDeleting}
          confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        >
          <Button
            variant="ghost"
            size="icon"
            disabled={isDeleting}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="size-4" />
            <span className="sr-only">Delete comparison</span>
          </Button>
        </DeleteConfirmationDialog>
      </TableCell>
    </TableRow>
  );
}
